/**
 * Always-on labels on the top hub nodes.
 *
 * Hand-positioned offset above each hub with a thin leader line drawn as
 * a small CSS line. Thin uppercase typography with wide tracking — Benzi
 * graph annotations.
 *
 * Excludes the center node (which has its own label in CenterAnchor).
 */
import { useEffect, useRef } from 'react';
import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { ClusterBy, Graph, GraphNode, Move, Position } from '../types/domain';
import type { ForceLayout } from './useForceLayout';
import { useAppStore } from '../state/store';
import { FOCUS_DIM, dimmedValue } from '../zones/palette';
import { labelAnchorOffset } from './sizing';
import { boostOf } from './interactBoost';

const DEFAULT_MAX_HUB_LABELS = 7;
// Numbers rather than strings: the dimmed value is derived from
// FOCUS_DIM before it reaches the DOM, so it has to be arithmetic first.
// 0.06 is where it was tuned at TUNED_FOCUS_DIM. Hub labels rest at 0.78
// rather than 1, which is why that is the ceiling — undimming returns
// this label to its own lit state, not to full white.
const DIM_OPACITY_TUNED = 0.06;
const BASE_OPACITY = 0.78;
const DIM_OPACITY = dimmedValue(DIM_OPACITY_TUNED, FOCUS_DIM, BASE_OPACITY);
const TMP_UP = new THREE.Vector3();

type Props = {
  graph: Graph;
  layout: ForceLayout;
  centerId: string;
  maxLabels?: number;
  glowNodes: Set<string>;
  clusterBy: ClusterBy;
  positionsById: Map<string, Position>;
  /** Kept on the prop signature for parity with Scene's wiring even
   *  though HubLabels no longer reads it — the cluster-in-title
   *  suffix that consumed this map was removed. */
  movesById: Map<string, Move>;
};

// Single-glyph position-type indicator. See ProximityLabels.tsx for the
// full rationale — same symbols, same suppression rule.
const POSITION_TYPE_SYMBOL: Record<string, string> = {
  top: '↑', bottom: '↓', neutral: '↔', flux: '⟳', terminal: '⊥',
};
function positionTypeSymbol(p: Position): string | null {
  if (p.tags?.includes('symmetric')) return POSITION_TYPE_SYMBOL.neutral;
  const z = p.zone;
  if (z === 'submitted') return POSITION_TYPE_SYMBOL.terminal;
  if (z === 'scrambled') return POSITION_TYPE_SYMBOL.flux;
  if (z === 'standing' || z === 'clinch') return POSITION_TYPE_SYMBOL.neutral;
  if (z.endsWith('-top')) return POSITION_TYPE_SYMBOL.top;
  if (z.endsWith('-bottom')) return POSITION_TYPE_SYMBOL.bottom;
  return null;
}

export function HubLabels({
  graph, layout, centerId, maxLabels, glowNodes,
  clusterBy, positionsById,
}: Props) {
  const limit = maxLabels ?? DEFAULT_MAX_HUB_LABELS;
  const hubs = graph.nodes
    .filter((n) => n.id !== centerId && (n.kind === 'position' || n.kind === 'move') && n.isHub)
    .sort((a, b) => b.weight - a.weight)
    .slice(0, limit);

  return (
    <>
      {hubs.map((n) => {
        let sym: string | undefined;
        if (clusterBy !== 'positionType' && n.kind === 'position') {
          const p = positionsById.get(n.id);
          if (p) sym = positionTypeSymbol(p) ?? undefined;
        }
        return (
          <HubLabel
            key={n.id}
            node={n}
            layout={layout}
            glowNodes={glowNodes}
            typeSymbol={sym}
          />
        );
      })}
    </>
  );
}

function HubLabel({
  node,
  layout,
  glowNodes,
  typeSymbol,
}: {
  node: GraphNode;
  layout: ForceLayout;
  glowNodes: Set<string>;
  typeSymbol?: string;
}) {
  const groupRef = useRef<THREE.Group>(null!);
  const innerGroupRef = useRef<THREE.Group>(null!);
  const labelRef = useRef<HTMLDivElement>(null!);
  const setSelection = useAppStore((s) => s.setSelection);
  // Ref-based so per-frame label writes don't trigger re-renders.
  const selectedIdRef = useRef<string | null>(null);
  const hoverIdRef = useRef<string | null>(null);
  useEffect(
    () =>
      useAppStore.subscribe((st) => {
        hoverIdRef.current = st.hoverId;
        const sel = st.selection;
        selectedIdRef.current =
          sel.kind === 'position'
            ? sel.id
            : sel.kind === 'move'
              ? sel.id
              : sel.kind === 'counter'
                ? `intercept:${sel.moveId}:${sel.id}`
                : null;
      }),
    [],
  );

  useFrame(({ camera }) => {
    if (!layout.posRef.current || !groupRef.current) return;
    const idx = layout.nodeIndex.get(node.id);
    if (idx === undefined) return;
    const pos = layout.posRef.current;
    groupRef.current.position.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);

    if (innerGroupRef.current) {
      TMP_UP.set(0, 1, 0).applyQuaternion(camera.quaternion);
      // Derived from the hub's real drawn radius (it varies a lot by
      // degree) plus any selection/hover inflation, instead of the flat
      // constant this used to use — which the biggest hubs outgrew.
      const off = labelAnchorOffset(node, {
          // Animated, so the label glides with the sphere
          // instead of jumping when selection changes.
          boost: boostOf(node.id),
      });
      innerGroupRef.current.position.set(
        TMP_UP.x * off,
        TMP_UP.y * off,
        TMP_UP.z * off,
      );
    }

    if (labelRef.current) {
      const isDimmed = glowNodes.size > 0 && !glowNodes.has(node.id);
      labelRef.current.style.opacity = String(
        isDimmed ? DIM_OPACITY : BASE_OPACITY,
      );
      // Selection cue #3 — the selected node's label goes bright and
      // full-weight. Same treatment ProximityLabels applies, so a hub and
      // a non-hub look identical when selected.
      const isSelected = selectedIdRef.current === node.id;
      labelRef.current.style.color = isSelected ? '#ffffff' : '';
      labelRef.current.style.fontWeight = isSelected ? '700' : '';
      labelRef.current.style.textShadow = isSelected
        ? '0 0 10px rgba(223,240,247,0.55)'
        : '';
    }
  });

  return (
    <group ref={groupRef}>
      {/* Inner group is offset from the node along screen-up in world
          space, so the label hugs the bridge ring at any zoom. */}
      <group ref={innerGroupRef}>
        <Html
          position={[0, 0, 0]}
          zIndexRange={[80, 0]}
          style={{ pointerEvents: 'none' }}
        >
          <div
            ref={labelRef}
            onClick={(e) => {
              e.stopPropagation();
              if (node.kind === 'position') setSelection({ kind: 'position', id: node.id });
              else if (node.kind === 'move') setSelection({ kind: 'move', id: node.id });
            }}
            style={{
              pointerEvents: 'auto',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              color: '#cad3df',
              fontSize: 10,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 300,
              opacity: 0.78,
              textShadow: '0 0 6px rgba(0,0,0,0.8)',
              transform: 'translate(-50%, calc(-100% - 4px))',
              transition: 'opacity 160ms ease',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            {node.label}
            {typeSymbol ? (
              <span
                style={{
                  opacity: 0.7,
                  marginLeft: 5,
                  fontSize: 11,
                  fontWeight: 400,
                  letterSpacing: 'normal',
                  color: '#e6e8ef',
                }}
                aria-label="position type"
              >
                {typeSymbol}
              </span>
            ) : null}
          </div>
        </Html>
      </group>
    </group>
  );
}
